import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { Country } from './country.resource';
import { Firm } from './firm.resource';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import { AtomService } from '../atom.service';
import {
  BooleanFilter,
  EnumListFilter,
  NumberFilter,
  ResourceListFilter,
  StringFilter,
} from 'src/app/filter/filter';
import { Enum } from './enum.resource';
import { Lang } from './lang.resource';
import { Product } from './product.resource';
import { LegalDocumentVersion, LegalDocumentVersionConfig } from './legal-document-version.resource';
import { FirmConfig, CountryConfig, LangConfig, ProductConfig } from '../../shared/tools/search/search-config';
import { JobsIPRangesVersionsConfig } from './jobsiprangesversions.resource';

@Resource({
  type: 'legaldocuments',
})
export class LegalDocument extends AtomResource {
  @Attribute() name?: string;
  @Attribute({ key: 'document_type' }) documentType?: Enum ;
  @Attribute({ key: 'use_only_last_version' }) useOnlyLastVersion?: boolean;

  @Relationship() lang?: Lang;
  @Relationship() country?: Country;
  @Relationship() legalfirm?: Firm;
  @Relationship() products?: Product[];
  @Relationship() versions?: LegalDocumentVersion[];
}
export class LegalDocumentConfig implements ResourceConfig {
  tables = {
    includes: ['lang', 'country','legalfirm','products'],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main' }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [{ path: ['name'], class: 'mat-body-2'},{ path: ['documentType'], class: 'mat-body-1'},],
        header: 'Name / Type',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'name',
      },

      {
        columnDef: 'country',
        displayedDatas: [
          {
            path: ['country.name'],
            class: 'mat-body-2',
            translatable: true,
          },
          {
            path: ['country.id'],
            class: 'mat-body-1',
            translatable: false,
          },
        ],
        header: 'Country',
        translatable: true,
        displayed: true,
        link: {
          resource: 'countries',
          filter: 'country.id',
          icon: 'language',
        },
        options: {
          flag: 'country.id',
        },
        sort: 'country.name',
      },
      {
        columnDef: 'legalfirm',
        displayedDatas: [{ path: ['legalfirm.name'], class: 'mat-body-2'}],
        header: 'Legal Firm',
        displayed: true,
        link: {
          detail: true,
        },
        sort: 'legalfirm.name',
      },
      {
        columnDef: 'products',
        displayed: true,
        link: {
          resource: 'products',
          list: 'products',
          target: 'legaldocuments',
        },
        header: 'Products',
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        displayed: true,
      },
    ],
  };
  detail={
    header: {
      icon: 'local_police',
      title: [
        {
          path: ['name'],
          translatable: false,
        },
      ],
      subtitle: [
        {
          path: ['id', 'country.name', 'country.id'],
          translatable: false,
        },
      ],
    },
    elements: [{
      columns: true,
      showEmpty: true,
      list: [
        {
          icon: 'category',
          title: 'Document Type',
          APIKey: 'documentType',
          value: {
            path: ['documentType'],
          },
        },
        {
          icon: 'change_circle',
          title: 'Only Last Version',
          APIKey: 'useOnlyLastVersion',
          value: {
            path: ['useOnlyLastVersion'],
          },
        },
        {
          icon: 'language',
          title: 'Language',
          APIKey: 'lang',
          value: {
            path: ['lang.id','lang.name'],
          },
        },
        {
          icon: 'business',
          title: 'Legal Firm',
          APIKey: 'legalfirm',
          value: {
            path: ['legalfirm.name'],
          },
          link: {
            resource: 'firms',
            filter: 'legalfirm.id',
          },
        }
      ]
    }],
    tabs: [
      {
        title: 'Versions',
        elements: [
          {
            resource: {
              APIKey: 'versions',
              config: new LegalDocumentVersionConfig(this.atom),
              multiple: true,
            },
          }
        ]
      }
    ]
  };
  wizard = {
    noCloseAfterCreation: false,
    tabs: [
      {
        title: 'Legal Document',
        formGroup: 'general',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            required: true
          },
          {
            type: 'enum',
            title: 'Document type',
            formControl: 'documentType',
            APIKey: 'documentType',
            fieldKey: 'document_type',
            required: true,
          },
          {
            type: 'resource',
            title: 'Lang',
            formControl: 'lang',
            search: new LangConfig(this.atom),
            required: true,
            activatingSequence: [
              {
                group: 'general',
                control: 'country',
                type: 'resource',
                filters: {
                  filter: 'equal.langs.id',
                },
              },
            ],
          },
          {
            type: 'resource',
            title: 'Country',
            formControl: 'country',
            APIKey: 'country',
            search: new CountryConfig(this.atom),
          },
          {
            type: 'resource',
            title: 'Legal Firm',
            formControl: 'legalfirm',
            APIKey: 'legalfirm',
            search: new FirmConfig(this.atom),
          },
          {
            type: 'boolean',
            title: 'Use Only Last Version',
            formControl: 'useOnlyLastVersion',
            APIKey: 'useOnlyLastVersion',
            required: true,
          }
        ]
      },
      {
        title: 'Products',
        formGroup: 'products',
        list: [
          {
            type: 'resource',
            title: 'Products',
            formControl: 'products',
            APIKey: 'products',
            search: new ProductConfig(this.atom,true),
            noCols: true,
          }]
      }
    ]
  }

  altResourceCreation = {
    config: new LegalDocumentVersionConfig(this.atom),
    link: {
      formControl: 'version',
      value: 'versions',
    },
    uniq: true,
  };

  resource = LegalDocument;
  resourceName = 'Legal Document';
  resourceKey = 'legaldocuments';
  resourceKeySingle = 'legaldocument';
  includes = [
    'lang',
    'country',
    'legalfirm',
    'products',
    'versions'
  ];
  filters = [
    new NumberFilter('id', 'ID'),
    new StringFilter('name', 'Name',true),
    new EnumListFilter(this.atom, 'legaldocuments', 'document_type', 'document_type','document Type'),
    new BooleanFilter('use_only_last_version',"Only Last Version"),
    new StringFilter('lang.id', 'Language ID'),
    new StringFilter('lang.name', 'Language  name'),
    new StringFilter('country.id', 'Country ID'),
    new StringFilter('country.name', 'Country name'),
    new NumberFilter('legalfirm.id', 'Handler firm ID'),
    new StringFilter('legalfirm.name', 'Handler firm name'),
    new NumberFilter('products.id', 'Products ID'),
    new StringFilter('products.name', 'Products name'),
    new NumberFilter('versions.id', 'Version ID'),

  ];
  constructor(private atom: AtomService, public hosted: string = 'products') {}
}
